import { ButtonSecondary } from "@nova/component-btn"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { NovaSpinner } from "@nova/component-nova-spinner"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { missionGroupColors, missionGroupIcons, ShareContact } from "@nova/schemas"
import * as Icons from "@nova/component-icons"
import { ContextMenu } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useCallback, useMemo } from "react"
import { setGlobalNotificationContext } from "user/profile/slice"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { setActiveContextMenu } from "features/popups/slice"
import {
    useConfirmationModalContext,
    getGenericDangerConfirmationModalComponent,
} from "features/modals/WorkspaceConfirmationModalV2"
import { MissionGroupIcon } from "./MissionGroupIcon"
import { CreateMissionGroupModal, ManageMissionGroupModal } from "./MissionGroupModals"
import { deleteList as deleteListAction } from "./slice"
import { useContactListFunctions, useContactLists } from "./useContactListFunctions"
import { zIndex } from "@nova/constants"

export const MissionGroupsManagePanel = () => {
    const dispatch = useAppDispatch()
    const { deleteList } = useContactListFunctions()
    const confirmationModal = useConfirmationModalContext()
    const manageMissionGroupContextMenu = useAppSelector(
        selectActiveContextMenu("ManageMissionGroup")
    )

    const { contactLists, isFetching } = useContactLists()

    const handleContextMenuToggle = useCallback(
        (e: React.MouseEvent<HTMLElement>, groupId: string) => {
            e.stopPropagation()
            if (manageMissionGroupContextMenu) {
                dispatch(setActiveContextMenu(undefined))
                return
            }
            const rect = e.currentTarget.getBoundingClientRect()
            dispatch(
                setActiveContextMenu({
                    type: "ManageMissionGroup",
                    position: { left: rect.left, top: rect.top },
                    data: { groupId },
                })
            )
        },
        [dispatch, manageMissionGroupContextMenu]
    )

    const contextMenuSections = useMemo(
        () =>
            manageMissionGroupContextMenu
                ? [
                      [
                          {
                              title: "Manage",
                              Icon: Icons.Pencil,
                              onClick: () => {
                                  const group = contactLists.find(
                                      (g) => g.id === manageMissionGroupContextMenu.data.groupId
                                  )
                                  if (!group) return
                                  const emails = group.contacts
                                      .filter(
                                          (c): c is Extract<ShareContact, { type: "email" }> =>
                                              c.type === "email"
                                      )
                                      .map((c) => c.email)
                                  const phones = group.contacts
                                      .filter(
                                          (c): c is Extract<ShareContact, { type: "phone" }> =>
                                              c.type === "phone"
                                      )
                                      .map((c) => c.phoneNumber)
                                  confirmationModal.confirm({
                                      RenderComponent: ({ confirm, cancel }) => (
                                          <ManageMissionGroupModal
                                              confirm={confirm}
                                              cancel={cancel}
                                              missionGroupId={group.id}
                                              initialListName={group.name}
                                              initialSelectedEmails={emails}
                                              initialSelectedPhoneNumbers={phones}
                                              initialSelectedColor={
                                                  group.color ?? missionGroupColors[0]
                                              }
                                              initialSelectedIcon={
                                                  group.icon ?? missionGroupIcons[0]
                                              }
                                          />
                                      ),
                                  })
                              },
                          },
                      ],
                      [
                          {
                              title: "Delete",
                              Icon: Icons.Trash,
                              isDestructive: true,
                              onClick: () => {
                                  const group = contactLists.find(
                                      (g) => g.id === manageMissionGroupContextMenu.data.groupId
                                  )
                                  if (!group) return
                                  confirmationModal.confirm({
                                      RenderComponent: getGenericDangerConfirmationModalComponent({
                                          title: `Delete ${group.name}?`,
                                          message:
                                              "Are you sure you want to delete this Mission Group?",
                                          dangerMessage: "This cannot be undone.",
                                          confirmText: "Delete Group",
                                      }),
                                      asyncConfirmCallback: async () => {
                                          try {
                                              await deleteList(group.id)
                                              dispatch(
                                                  deleteListAction({ contactListId: group.id })
                                              )
                                              dispatch(
                                                  setGlobalNotificationContext({
                                                      message: `${group.name} has been deleted.`,
                                                      status: GlobalNotificationStatus.success,
                                                      timeoutInMs: 3000,
                                                  })
                                              )
                                          } catch {
                                              dispatch(
                                                  setGlobalNotificationContext({
                                                      message: `Failed to delete ${group.name}. Please try again.`,
                                                      status: GlobalNotificationStatus.error,
                                                      timeoutInMs: 5000,
                                                  })
                                              )
                                          }
                                      },
                                  })
                              },
                          },
                      ],
                  ]
                : [],
        [confirmationModal, contactLists, deleteList, dispatch, manageMissionGroupContextMenu]
    )

    return (
        <div className="flex flex-col gap-2 overflow-hidden">
            <div className="p-2.5 border border-gray-700 rounded-lg min-h-0 shrink overflow-hidden">
                {isFetching && (
                    <div className="flex p-6 justify-center items-center">
                        <NovaSpinner width={32} height={32} />
                    </div>
                )}
                {!isFetching && (
                    <ScrollableArea className="h-full">
                        <div className="flex flex-col gap-0.5">
                            {contactLists.map((group) => (
                                <div key={group.id} className="px-2 py-1 flex gap-3 items-center">
                                    <MissionGroupIcon groupId={group.id} size="32" />
                                    <div className="grow">{group.name}</div>
                                    <Icons.Icon
                                        Icon={Icons.ThreeDotsSettings}
                                        className="text-gray-100 hover:text-gray-50 cursor-pointer"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleContextMenuToggle(e, group.id)
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </ScrollableArea>
                )}
            </div>
            <ButtonSecondary
                title="Create Mission Group"
                LeftIcon={Icons.Plus}
                onClick={() =>
                    confirmationModal.confirm({ RenderComponent: CreateMissionGroupModal })
                }
            />
            {manageMissionGroupContextMenu && (
                <ContextMenu
                    isFixedPosition
                    top={manageMissionGroupContextMenu.position.top}
                    left={manageMissionGroupContextMenu.position.left}
                    onBlur={() => dispatch(setActiveContextMenu(undefined))}
                    sections={contextMenuSections}
                    style={{ zIndex: zIndex.HighPriorityModalDropdown }}
                />
            )}
        </div>
    )
}
